import ContactCommunicationDialogs from "../components/messages/ContactCommunicationDialogs";
// src/layouts/SidebarLayout.jsx
import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Drawer,
  Toolbar,
  AppBar,
  IconButton,
  Typography,
  Grid,
  Stack,
  Popover,
  Snackbar,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";

// ✅ Iconsax
import {
  HambergerMenu,
  SearchNormal1,
  CloseCircle,
} from "iconsax-react";

import HeaderQuickActions from "../components/HeaderQuickActions";
import SidebarNav from "../components/SidebarNav";
import TopBar from "../components/TopBar";
import { logoutUser } from "../controllers/authController";
import HeaderProfile from "../components/HeaderProfile";
import { readStoredUser, writeStoredUser } from "../utils/userSession";
import { getSidebarResetToken } from "../utils/sidebarNavigation";
import {
  AI_AGENT_WORK_STATE_EVENT,
  clearAiAgentWorkState,
  readAiAgentWorkState,
  writeAiAgentWorkState,
} from "../utils/aiAgentWorkState";
import {
  ENTITY_BACKGROUND_WORK_EVENT,
  getEntityBackgroundWork,
  replaceEntityBackgroundWork,
} from "../utils/entityBackgroundWorkState";
import { isActivePendingFlag } from "../utils/recruiterOwnership";

const drawerWidth = 109;
const desktopSidebarRailWidth = drawerWidth + 12;

const getAuthToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("jwt") ||
  "";

function AIAgentWorkFeedback({ userId }) {
  const normalizedUserId = String(userId || "anon");
  const [workState, setWorkState] = useState(() => readAiAgentWorkState(normalizedUserId));

  useEffect(() => {
    setWorkState(readAiAgentWorkState(normalizedUserId));

    const sync = (event) => {
      if (String(event?.detail?.userId || normalizedUserId) !== normalizedUserId) return;
      setWorkState(event?.detail?.state || readAiAgentWorkState(normalizedUserId));
    };

    window.addEventListener(AI_AGENT_WORK_STATE_EVENT, sync);
    return () => window.removeEventListener(AI_AGENT_WORK_STATE_EVENT, sync);
  }, [normalizedUserId]);

  useEffect(() => {
    if (workState?.status !== "working") return undefined;

    let active = true;
    const startedAt = new Date(workState.startedAt || 0).getTime();
    const checkForCompletedWork = async () => {
      const token = getAuthToken();
      if (!token) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE || ""}/api/ai-agent/messages`, {
          headers: { Authorization: `Bearer ${token}`, "x-access-token": token },
        });
        const payload = await response.json().catch(() => ({}));
        if (!active || !response.ok) return;
        const hasNewAssistantMessage = (Array.isArray(payload?.items) ? payload.items : []).some(
          (item) =>
            String(item?.role || "").toLowerCase() === "assistant" &&
            new Date(item?.created_at || item?.createdAt || 0).getTime() >= startedAt
        );
        if (hasNewAssistantMessage) {
          writeAiAgentWorkState(normalizedUserId, {
            ...workState,
            status: "completed",
            completedAt: new Date().toISOString(),
            message:
              workState.mode === "search"
                ? "AI Agent finished the search."
                : "AI Agent finished your request.",
          });
        }
      } catch {
        // Keep the working state visible and retry after navigation or a transient network failure.
      }
    };

    checkForCompletedWork();
    const intervalId = window.setInterval(checkForCompletedWork, 5000);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [normalizedUserId, workState]);

  const dismiss = () => {
    if (workState?.status === "working") return;
    clearAiAgentWorkState(normalizedUserId);
  };
  const severity = workState?.status === "failed" ? "error" : workState?.status === "completed" ? "success" : "info";

  return (
    <Snackbar
      open={Boolean(workState?.status)}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      autoHideDuration={workState?.status === "working" ? null : 8000}
      onClose={dismiss}
    >
      <Alert
        severity={severity}
        variant="filled"
        onClose={workState?.status === "working" ? undefined : dismiss}
        icon={workState?.status === "working" ? <CircularProgress size={18} color="inherit" /> : undefined}
        sx={{ alignItems: "center", maxWidth: 420 }}
      >
        {workState?.message || "AI Agent is working on your request..."}
      </Alert>
    </Snackbar>
  );
}

function GlobalEntityBackgroundWorkFeedback({ apiBase }) {
  const [tasks, setTasks] = useState(() => getEntityBackgroundWork());

  useEffect(() => {
    const sync = (event) => setTasks(event?.detail?.tasks || getEntityBackgroundWork());
    window.addEventListener(ENTITY_BACKGROUND_WORK_EVENT, sync);
    return () => window.removeEventListener(ENTITY_BACKGROUND_WORK_EVENT, sync);
  }, []);

  useEffect(() => {
    if (!tasks.length) return undefined;

    let active = true;
    const refreshTasks = async () => {
      const token = getAuthToken();
      const nextTasks = await Promise.all(
        tasks.map(async (task) => {
          if (!task?.entityId) return task;
          try {
            const isCompany = task?.entityType === "company";
            const isJob = task?.entityType === "job";
            if (!isCompany && !isJob) return task;
            const response = await fetch(
              `${String(apiBase || "").replace(/\/$/, "")}/api/${isCompany ? "companies" : "jobs"}/${encodeURIComponent(String(task.entityId))}`,
              {
                headers: token ? { Authorization: `Bearer ${token}`, "x-access-token": token } : {},
                credentials: "include",
              }
            );
            // The banner is an indicator of confirmed server-side work. Do not
            // leave a stale local task visible when its status cannot be read.
            if (!response.ok) return null;
            const payload = await response.json().catch(() => ({}));
            const entity = isCompany ? payload?.company || payload || {} : payload?.job || payload || {};
            const contactRepairPending = isActivePendingFlag(entity?.contact_repair_pending);
            const profileEnrichmentPending = isActivePendingFlag(entity?.company_profile_enrichment_pending);
            const jobDetailsEnrichmentPending = isActivePendingFlag(entity?.job_details_enrichment_pending);
            if (isJob) {
              return jobDetailsEnrichmentPending ? { ...task, jobDetailsEnrichmentPending } : null;
            }
            return contactRepairPending || profileEnrichmentPending
              ? { ...task, contactRepairPending, profileEnrichmentPending }
              : null;
          } catch {
            // Without a successful status response, do not claim the AI is
            // still running in the background.
            return null;
          }
        })
      );
      const normalizedNextTasks = nextTasks.filter(Boolean);
      if (
        active &&
        JSON.stringify(normalizedNextTasks) !== JSON.stringify(tasks)
      ) {
        replaceEntityBackgroundWork(normalizedNextTasks);
      }
    };

    refreshTasks();
    const intervalId = window.setInterval(refreshTasks, 3000);
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, [apiBase, tasks]);

  if (!tasks.length) return null;
  const task = tasks[0];
  const details = task?.entityType === "job"
    ? [task?.jobDetailsEnrichmentPending ? "enriching job details" : ""].filter(Boolean)
    : [
        task?.profileEnrichmentPending ? "enriching the company profile" : "",
        task?.contactRepairPending ? "refreshing company contacts" : "",
      ].filter(Boolean);

  return (
    <Box
      sx={{
        mx: { xs: 1.5, sm: 2.5 },
        mt: 1.5,
        px: 2,
        py: 1.15,
        border: "1px solid #E9D8FD",
        borderRadius: 2,
        backgroundColor: "#FAF5FF",
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <CircularProgress size={16} thickness={5} sx={{ color: "#7C3AED", flexShrink: 0 }} />
      <Typography sx={{ color: "#5B21B6", fontSize: 12.5, fontWeight: 700 }}>
        AI is working in the background for {task?.entityName || "an item"}
        {details.length ? `: ${details.join(" and ")}.` : "."}
        {tasks.length > 1 ? ` ${tasks.length - 1} more task${tasks.length === 2 ? "" : "s"} running.` : ""}
      </Typography>
    </Box>
  );
}

export default function SidebarLayout({ children }) {
  const [user, setUser] = useState(() => readStoredUser());
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // ---- Mobile search popup state ----
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchAnchor, setSearchAnchor] = useState(null);
  const [query, setQuery] = useState("");
  const debounceRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE || "";
  const logoSrc = require("../assets/rpo_logo.png");

  useEffect(() => {
    const syncStoredUser = (event) => {
      if (event?.type === "rpo:user-updated") {
        setUser(event.detail || null);
        return;
      }

      setUser(readStoredUser());
    };

    window.addEventListener("storage", syncStoredUser);
    window.addEventListener("rpo:user-updated", syncStoredUser);

    return () => {
      window.removeEventListener("storage", syncStoredUser);
      window.removeEventListener("rpo:user-updated", syncStoredUser);
    };
  }, []);

  const handleLogout = async () => {
    try { await logoutUser(); writeStoredUser(null); navigate("/"); }
    catch (error) { window.alert(error.message); }
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);


  const drawerContent = (
    <SidebarNav
      user={user}
      onLogout={handleLogout}
      closeDrawer={() => setMobileOpen(false)}
    />
  );

  // -------------------- Mobile actions --------------------
  const handleLogoClick = () => navigate("/overview");

  // -------------------- Mobile search popup logic --------------------
  const openSearch = (event) => {
    setSearchAnchor(event.currentTarget);
    setSearchOpen(true);
  };

  const closeSearch = () => {
    setSearchOpen(false);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  // ✅ Redirect to desktop search page route:
  // /search?q=...
  const redirectToSearchPage = (q) => {
    const trimmed = (q || "").trim();
    closeSearch();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  const onQueryChange = (val) => {
    setQuery(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // keep manual submit (Enter) like before
    }, 250);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    redirectToSearchPage(query);
  };

  const pageResetToken = getSidebarResetToken(location, location.pathname);
  const pageContentKey = `${location.pathname}::${pageResetToken || "default"}`;
  const renderedPageContent = children
    ? React.isValidElement(children)
    ? React.cloneElement(children, {
        key: pageContentKey,
        user,
      })
    : children
    : <Outlet key={pageContentKey} />;


  const iconProps = { size: 22, color: "#FFFFFF", variant: "Linear" };

  return (
    <>
    <Grid className="page-wrapper" container spacing={0} justifyContent="center">
      <Grid
        item
        xs={12}
        sx={{
          flexBasis: { xs: "100%", sm: `${desktopSidebarRailWidth}px` },
          maxWidth: { xs: "100%", sm: `${desktopSidebarRailWidth}px` },
          width: { xs: "100%", sm: `${desktopSidebarRailWidth}px` },
          flexShrink: 0,
          position: "relative",
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
      >
        {/* ✅ Mobile AppBar */}
        <AppBar
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            display: { xs: "flex", sm: "none" },
            bgcolor: "#9c4fff",
          }}
        >
          <Toolbar sx={{ gap: 0.25, minHeight: 64, px: "4px !important", flexWrap: "wrap", "& > .MuiIconButton-root": { p: 0.5 } }}>
            {/* ✅ Logo BEFORE hamburger */}
            <IconButton
              onClick={handleLogoClick}
              aria-label="go to overview"
              sx={{ p: 0.5, borderRadius: 2 }}
            >
              <Box
                component="img"
                src={logoSrc}
                alt="Logo"
                sx={{ height: 28, width: "auto", display: "block" }}
              />
            </IconButton>

            {/* Hamburger */}
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 0.25 }}
              aria-label="open menu"
            >
              <HambergerMenu {...iconProps} />
            </IconButton>

            {/* Search icon opens popup */}
            <IconButton
              color="inherit"
              onClick={openSearch}
              sx={{ mr: "auto" }}
              aria-label="search"
            >
              <SearchNormal1 {...iconProps} />
            </IconButton>

            {/* Right: icon-only actions */}
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <HeaderQuickActions user={user} variant="mobile" />

              <HeaderProfile user={user} onProfileMenuOpen={handleProfileMenuOpen} mobile />
            </Stack>
          </Toolbar>
        </AppBar>

        {/* ✅ Mobile Search Popover (redirects to /search?q=...) */}
        <Popover
          open={searchOpen}
          anchorEl={searchAnchor}
          onClose={closeSearch}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              mt: 1,
              width: "calc(100vw - 20px)",
              maxWidth: 520,
              borderRadius: 2,
              overflow: "hidden",
            },
          }}
        >
          <Box sx={{ p: 1.25 }}>
            <Box data-popup-header
              component="form"
              onSubmit={onSubmit}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <TextField
                autoFocus
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder="Search..."
                size="small"
                fullWidth
              />
              <IconButton data-popup-close type="button" onClick={closeSearch} aria-label="close search">
                <CloseCircle size={24} color="#111827" variant="Bold" />
              </IconButton>
            </Box>

            <Box sx={{ mt: 1, px: 0.25 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Press Enter to search
              </Typography>
            </Box>
          </Box>
        </Popover>

        {/* Sidebar Drawer */}
        <Box className="sidebar" component="nav">
          <Drawer
            className="mobile-drawer"
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", sm: "none" },
              "& .MuiDrawer-paper": { width: drawerWidth, height: "100vh" },
            }}
          >
            {drawerContent}
          </Drawer>

          <Drawer
            className="sidebar-drawer"
            variant="permanent"
            sx={{
              display: { xs: "none", sm: "block" },
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                boxSizing: "border-box",
                backgroundImage: `url(${require("../assets/rpo-aside-bg.png")})`,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                borderRadius: "8px",
                marginTop: "5px",
                marginLeft: "3px",
                height: "calc(100dvh + 90px)",
                overflow: "hidden",
                position: "relative",
                zIndex: (theme) => theme.zIndex.drawer + 2,
                pointerEvents: "auto",
              },
            }}
            open
          >
            {drawerContent}
          </Drawer>
        </Box>
      </Grid>

      <Grid
        className="content-grid right-column"
        item
        xs={12}
        sx={{
          flex: { xs: "1 1 100%", sm: "1 1 0%" },
          minWidth: 0,
          width: { xs: "100%", sm: "auto" },
          maxWidth: {
            xs: "100%",
            sm: `calc(100% - ${desktopSidebarRailWidth + 15}px)`,
          },
          marginLeft: { xs: 0, sm: "15px" },
          position: "relative",
          zIndex: 0,
        }}
      >
        <Box className="main-content" component="main" sx={{ flexGrow: 1 }}>
          <Toolbar sx={{ display: { xs: "block", sm: "none" } }} />

          {/* ✅ Hide TopBar on mobile */}
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <TopBar
              user={user}
              onLogout={handleLogout}
              anchorEl={anchorEl}
              onProfileMenuOpen={handleProfileMenuOpen}
              onProfileMenuClose={handleProfileMenuClose}
            />
          </Box>

          <Box key={pageContentKey} sx={{ minWidth: 0, mt: { xs: "30px", sm: 0 } }}>
            <GlobalEntityBackgroundWorkFeedback apiBase={API_BASE} />
            {renderedPageContent}
          </Box>
        </Box>
      </Grid>
    </Grid>
    <ContactCommunicationDialogs />
    <AIAgentWorkFeedback userId={user?.id} />
    </>
  );
}
